import usePrivateAxios from '@/common/api/usePrivateAxios';
import { defaultTransformerResponse } from '@/common/constants/defaultTransformerResponse.constant';
import { Mapper } from '@/common/services/mapper';
import { MetaResponse } from '@/common/types/metaResponse';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ManualEntryProposalColumnStatus } from '../types/maintenanceManualEntryProposal.type';

type UseMaintenanceManualEntryProposalsProps<T> = {
  filters?: {
    search?: string;
    page?: number;
    status?: ManualEntryProposalColumnStatus;
    directiveEntryType?: string;
    clientCompanyId?: string;
    createdAtGteq?: string;
    createdAtLteq?: string;
    completedAtGteq?: string;
    completedAtLteq?: string;
    canceledAtGteq?: string;
    canceledAtLteq?: string;
  };
  mapper?: (data: any) => T;
  enabled?: boolean;
}

export const useMaintenanceManualEntryProposals = <T,>({
  filters: {
    search = '',
    page = 1,
    status,
    directiveEntryType,
    clientCompanyId,
    createdAtGteq,
    createdAtLteq,
    completedAtGteq,
    completedAtLteq,
    canceledAtGteq,
    canceledAtLteq,
  } = {},
  mapper = Mapper.default<T>(),
  enabled,
}: UseMaintenanceManualEntryProposalsProps<T>) => {
  const privateAxios = usePrivateAxios()

  const { data: { data, meta } = defaultTransformerResponse<T>(), ...rest } = useQuery({
    queryKey: [
      'maintenanceManualEntryProposals',
      search,
      page,
      status,
      directiveEntryType,
      clientCompanyId,
      createdAtGteq,
      createdAtLteq,
      completedAtGteq,
      completedAtLteq,
      canceledAtGteq,
      canceledAtLteq,
    ],
    queryFn: () => {
      return privateAxios.get(`/maintenance/establishment_choice/manual_entry_proposals`, {
        params: {
          search,
          page,
          'q[status_eq]': status,
          'q[directive_entry_type_eq]': directiveEntryType,
          'q[client_company_id_eq]': clientCompanyId,
          'q[created_at_gteq]': createdAtGteq,
          'q[created_at_lteq]': createdAtLteq,
          'q[completed_at_gteq]': completedAtGteq,
          'q[completed_at_lteq]': completedAtLteq,
          'q[canceled_at_gteq]': canceledAtGteq,
          'q[canceled_at_lteq]': canceledAtLteq,
        },
      })
    },
    select: (data: { data: { data: any[]; meta: MetaResponse } }) => {
      return {
        data: mapper(data?.data?.data),
        meta: data?.data?.meta,
      }
    },
    placeholderData: keepPreviousData,
    enabled: typeof enabled === 'boolean' ? enabled : true,
  });

  return { data, meta, ...rest }
}
